import { useSyncExternalStore } from "react";

/**
 * Whether this person closed the support chat bubble. Shown by default; closing
 * it is remembered in this browser, and Help -> "Contact support" brings it back.
 * When storage cannot be written (private mode, blocked site data, quota), the
 * choice is still kept for this tab, so the close button always works.
 */
const KEY = "fastn:support-chat-hidden";
const CHANGE_EVENT = "fastn:support-chat-visibility";
let memoryHidden = false;
// Set once a write fails: from then on storage may hold a stale value, or none.
let storageUnusable = false;

export function isSupportChatHidden(): boolean {
  if (storageUnusable) return memoryHidden;
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return memoryHidden;
  }
}

export function setSupportChatHidden(hidden: boolean): void {
  memoryHidden = hidden;
  try {
    if (hidden) window.localStorage.setItem(KEY, "1");
    else window.localStorage.removeItem(KEY);
  } catch {
    storageUnusable = true;
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function useSupportChatHidden(): boolean {
  return useSyncExternalStore(subscribe, isSupportChatHidden, () => false);
}
